import type { PermissionKey, Role } from '@obras/shared';
import { Link, useRouterState } from '@tanstack/react-router';
import {
  Building2,
  Landmark,
  ChartLine,
  ChevronsUpDown,
  HardDrive,
  HardHat,
  LayoutDashboard,
  LogOut,
  Menu,
  Repeat,
  Settings,
  ShieldCheck,
  SquareKanban,
  Users,
  UserRound,
  X,
  type LucideIcon,
} from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';

import { useAuth } from '../lib/auth.js';
import { cn } from '../lib/cn.js';
import { BrandLockup } from './Brand.js';
import { Avatar } from './ui/Feedback.js';

const ICON = { strokeWidth: 1.5 } as const;

interface NavItem {
  readonly to: string;
  readonly label: string;
  readonly icon: LucideIcon;
  /** Marcado quando a rota ainda não existe — evita link morto silencioso. */
  readonly phase?: string | undefined;
  /**
   * Some do menu para quem não alcança o papel ou não tem a permissão.
   *
   * É conforto visual, não segurança: a permissão de verdade é verificada no
   * servidor a cada requisição.
   */
  readonly minimumRole?: Role | undefined;
  readonly permission?: PermissionKey | undefined;
}

interface NavGroup {
  readonly label: string;
  readonly items: readonly NavItem[];
}

/** Menu do painel de um órgão. */
const ORGANIZATION_NAVIGATION: readonly NavGroup[] = [
  {
    label: 'Acompanhamento',
    items: [
      { to: '/', label: 'Dashboard', icon: LayoutDashboard },
      { to: '/obras', label: 'Obras', icon: HardHat },
      {
        to: '/tarefas',
        label: 'Tarefas',
        icon: SquareKanban,
        permission: 'canAccessTasks',
      },
    ],
  },
  {
    label: 'Cadastros',
    items: [
      { to: '/empresas', label: 'Empresas', icon: Building2 },
      {
        to: '/secretarias',
        label: 'Secretarias',
        icon: Landmark,
        permission: 'canAccessSecretarias',
      },
      { to: '/usuarios', label: 'Usuários', icon: Users, minimumRole: 'gestor' },
    ],
  },
  {
    label: 'Gestão',
    items: [
      {
        to: '/relatorios',
        label: 'Relatórios',
        icon: ChartLine,
        permission: 'canAccessReports',
      },
      {
        to: '/auditoria',
        label: 'Auditoria',
        icon: ShieldCheck,
        permission: 'canAccessAudit',
      },
      { to: '/consumo', label: 'Consumo', icon: HardDrive, minimumRole: 'gestor' },
      { to: '/configuracoes', label: 'Configurações', icon: Settings, minimumRole: 'gestor' },
    ],
  },
];

/** Menu da área da plataforma. Só organizações e pessoas — nada operacional. */
const PLATFORM_NAVIGATION: readonly NavGroup[] = [
  {
    label: 'Plataforma',
    items: [
      { to: '/plataforma', label: 'Organizações', icon: Building2 },
      { to: '/plataforma/usuarios', label: 'Usuários', icon: Users },
    ],
  },
];

function isActive(currentPath: string, target: string): boolean {
  if (target === '/' || target === '/plataforma') return currentPath === target;
  return currentPath === target || currentPath.startsWith(`${target}/`);
}

function SidebarNav({ onNavigate }: { onNavigate?: () => void }) {
  const currentPath = useRouterState({ select: (state) => state.location.pathname });
  const { can, may, inPlatform } = useAuth();

  const groups = (inPlatform ? PLATFORM_NAVIGATION : ORGANIZATION_NAVIGATION)
    .map((group) => ({
      ...group,
      items: group.items.filter(
        (item) =>
          (!item.minimumRole || can(item.minimumRole)) &&
          (!item.permission || may(item.permission)),
      ),
    }))
    .filter((group) => group.items.length > 0);

  return (
    <nav className="flex flex-col gap-5 px-3 py-2" aria-label="Navegação principal">
      {groups.map((group) => (
        <div key={group.label}>
          <p className="mb-1 px-3 text-[12px] text-muted-foreground">{group.label}</p>

          <ul className="flex flex-col gap-0.5">
            {group.items.map((item) => {
              const active = isActive(currentPath, item.to);
              const Icon = item.icon;

              if (item.phase) {
                return (
                  <li key={item.to}>
                    <span
                      className="flex h-9 cursor-not-allowed items-center gap-2.5 rounded-md px-3 text-sm text-muted-foreground/70"
                      title={`Disponível na ${item.phase}`}
                    >
                      <Icon {...ICON} size={17} aria-hidden />
                      {item.label}
                    </span>
                  </li>
                );
              }

              return (
                <li key={item.to}>
                  <Link
                    to={item.to}
                    onClick={onNavigate}
                    aria-current={active ? 'page' : undefined}
                    className={cn(
                      'flex h-9 items-center gap-2.5 rounded-md px-3 text-sm transition-colors duration-100',
                      active
                        ? 'bg-accent font-medium text-foreground'
                        : 'text-muted-foreground hover:bg-muted hover:text-foreground',
                    )}
                  >
                    <Icon
                      {...ICON}
                      size={17}
                      className={active ? 'text-primary' : undefined}
                      aria-hidden
                    />
                    {item.label}
                  </Link>
                </li>
              );
            })}
          </ul>
        </div>
      ))}
    </nav>
  );
}

/**
 * Conta do usuário e troca de ambiente.
 *
 * O painel sobreposto usa a superfície invertida: sem sombra e sem borda, é o
 * contraste de tom que o descola da página.
 */
function AccountMenu({ compact = false }: { compact?: boolean }) {
  const { user, organization, environments, logout, switchEnvironment, inPlatform } = useAuth();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    const close = () => setOpen(false);
    window.addEventListener('click', close);
    return () => window.removeEventListener('click', close);
  }, [open]);

  if (!user) return null;

  const currentEnvironment = inPlatform ? 'Administrador' : (organization?.name ?? '');
  const others = environments.filter((environment) =>
    inPlatform ? environment.kind !== 'platform' : environment.name !== organization?.name,
  );

  return (
    <div className="relative" onClick={(event) => event.stopPropagation()}>
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        aria-haspopup="menu"
        aria-label="Conta"
        className={cn(
          'flex items-center gap-2.5 rounded-md text-left transition-colors hover:bg-muted',
          compact ? 'size-10 justify-center' : 'h-12 w-full px-2',
        )}
      >
        <Avatar name={user.name} url={user.avatarUrl} size={compact ? 28 : 30} />
        {compact ? null : (
          <>
            <span className="min-w-0 flex-1 leading-tight">
              <span className="block truncate text-[13px] font-medium">{user.name}</span>
              <span className="block truncate text-[11px] text-muted-foreground">
                {currentEnvironment}
              </span>
            </span>
            <ChevronsUpDown
              {...ICON}
              size={15}
              className="shrink-0 text-muted-foreground"
              aria-hidden
            />
          </>
        )}
      </button>

      {open ? (
        <div
          role="menu"
          className={cn(
            'absolute z-50 w-64 overflow-hidden rounded-lg bg-foreground p-1 text-white',
            compact ? 'top-full right-0 mt-2' : 'bottom-full left-0 mb-2',
          )}
        >
          <div className="px-2.5 py-2">
            <p className="truncate text-[13px] font-medium">{user.name}</p>
            <p className="truncate font-mono text-[11px] text-white/50">{user.email}</p>
          </div>

          {others.length > 0 ? (
            <>
              <p className="px-2.5 pt-2 pb-1 text-[11px] text-white/45">Trocar de ambiente</p>
              {others.map((environment) => (
                <button
                  key={environment.id}
                  type="button"
                  role="menuitem"
                  onClick={() => switchEnvironment(environment.id)}
                  className="flex w-full items-center gap-2.5 rounded-md px-2.5 py-2 text-left text-[13px] transition-colors hover:bg-white/10"
                >
                  <Repeat {...ICON} size={15} aria-hidden />
                  <span className="truncate">{environment.name}</span>
                </button>
              ))}
              <div className="my-1 h-px bg-white/10" aria-hidden />
            </>
          ) : null}

          {/*
            Perfil acima de Sair, e separado dele por um filete: são as duas
            ações da própria conta, e a de baixo é irreversível na sessão.
          */}
          <Link
            to="/perfil"
            role="menuitem"
            onClick={() => setOpen(false)}
            className="flex w-full items-center gap-2.5 rounded-md px-2.5 py-2 text-left text-[13px] transition-colors hover:bg-white/10"
          >
            <UserRound {...ICON} size={15} aria-hidden />
            Perfil
          </Link>

          <div className="my-1 h-px bg-white/10" aria-hidden />

          <button
            type="button"
            role="menuitem"
            onClick={() => void logout()}
            className="flex w-full items-center gap-2.5 rounded-md px-2.5 py-2 text-left text-[13px] transition-colors hover:bg-white/10"
          >
            <LogOut {...ICON} size={15} aria-hidden />
            Sair
          </button>
        </div>
      ) : null}
    </div>
  );
}

export function AppShell({ children }: { children: ReactNode }) {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const { organization, inPlatform } = useAuth();

  const subtitle = inPlatform ? 'Administração da plataforma' : organization?.name;

  return (
    /* No papel a navegação não existe: sobra a página inteira para o conteúdo. */
    <div className="min-h-dvh bg-background lg:pl-[248px] print:pl-0">
      {/* Barra lateral de altura total: âncora espacial fixa da SPA. */}
      <aside className="fixed inset-y-0 left-0 z-40 hidden w-[248px] flex-col bg-card lg:flex print:hidden">
        <div className="flex h-16 shrink-0 items-center px-5">
          <Link to={inPlatform ? '/plataforma' : '/'} className="min-w-0">
            <BrandLockup subtitle={subtitle} />
          </Link>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto">
          <SidebarNav />
        </div>

        <div className="shrink-0 p-3">
          <AccountMenu />
        </div>
      </aside>

      <header className="sticky top-0 z-30 flex h-14 items-center gap-2 bg-card px-3 lg:hidden print:hidden">
        <button
          type="button"
          onClick={() => setDrawerOpen(true)}
          aria-label="Abrir menu"
          className="grid size-10 place-items-center rounded-md text-muted-foreground transition-colors hover:bg-muted"
        >
          <Menu {...ICON} size={20} aria-hidden />
        </button>

        <Link to={inPlatform ? '/plataforma' : '/'} className="min-w-0">
          <BrandLockup size={24} />
        </Link>

        <div className="ml-auto">
          <AccountMenu compact />
        </div>
      </header>

      {drawerOpen ? (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div
            className="absolute inset-0 bg-[rgba(24,24,27,0.45)]"
            onClick={() => setDrawerOpen(false)}
            aria-hidden
          />
          <div className="absolute inset-y-0 left-0 flex w-[272px] flex-col bg-card">
            <div className="flex h-14 shrink-0 items-center justify-between px-4">
              <BrandLockup size={24} subtitle={subtitle} />
              <button
                type="button"
                onClick={() => setDrawerOpen(false)}
                aria-label="Fechar menu"
                className="grid size-9 place-items-center rounded-md text-muted-foreground transition-colors hover:bg-muted"
              >
                <X {...ICON} size={18} aria-hidden />
              </button>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto">
              <SidebarNav onNavigate={() => setDrawerOpen(false)} />
            </div>
          </div>
        </div>
      ) : null}

      <main className="min-w-0">
        <div className="mx-auto max-w-[1400px] px-4 py-6 lg:px-8 lg:py-9 print:max-w-none print:px-2 print:py-0">
          {children}
        </div>
      </main>
    </div>
  );
}

export function PageHeader({
  title,
  description,
  action,
  breadcrumb,
}: {
  title: string;
  description?: string | undefined;
  action?: ReactNode | undefined;
  breadcrumb?: ReactNode | undefined;
}) {
  return (
    <div className="mb-6">
      {breadcrumb ? (
        <div className="mb-2.5 text-[13px] text-muted-foreground">{breadcrumb}</div>
      ) : null}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-[26px] font-semibold tracking-tight">{title}</h1>
          {description ? (
            <p className="mt-1 max-w-[68ch] text-[14px] text-muted-foreground">{description}</p>
          ) : null}
        </div>
        {action}
      </div>
    </div>
  );
}
